import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiUser, FiMail, FiBriefcase, FiHome, FiLayers, FiArrowRight, FiCheck, FiTrendingUp, FiMapPin } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  'Founder / CEO',
  'Executive / C-Suite',
  'Manager',
  'Employee',
  'Consultant',
  'Student',
  'Other'
];

const INDUSTRY_OPTIONS = [
  'Technology / Software',
  'Manufacturing',
  'Healthcare / Life Sciences',
  'Robotics / Automation',
  'Clean Energy / Sustainability',
  'Food & Beverage',
  'Retail / E-commerce',
  'Professional Services',
  'Education',
  'Non-profit',
  'Other'
];

const STAGE_OPTIONS = [
  'Just an idea',
  'Building an MVP / prototype',
  'Early stage (first customers)',
  'Growth stage (scaling)',
  'Established / mature',
];

export default function ProfileSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [industry, setIndustry] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [stage, setStage] = useState('');
  const [locationField, setLocationField] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load the latest profile so fields are pre-filled (create or edit anytime)
    fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(me => {
        if (!me) {
          if (!user && !localStorage.getItem('user')) navigate('/login');
          return;
        }
        setUser(me);
        setName(me.name || '');
        setEmail(me.email || '');
        setRole(me.role || '');
        setCompany(me.company || '');
        setIndustry(me.industry || '');
        setBusinessDescription(me.business_description || '');
        setStage(me.stage || '');
        setLocationField(me.location || '');
        setIsEditing(!!me.profile_completed);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          role: role || null,
          company: company || null,
          industry: industry || null,
          business_description: businessDescription || null,
          stage: stage || null,
          location: locationField || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Navigate to dashboard
      navigate('/dashboard', { state: { user: updatedUser } });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard', { state: { user } });
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8] flex flex-col">
      {/* Header */}
      <header className="bg-[#1e3a5f] py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <span className="font-bold text-xl text-[#f5f0e6]">ConnectMakeLaunch AI</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                <FiCheck className="text-white" />
              </div>
              <div className="w-12 h-1 bg-[#1e3a5f] rounded"></div>
              <div className="w-8 h-8 bg-[#c9a962] rounded-full flex items-center justify-center">
                <span className="text-[#1e3a5f] font-bold text-sm">2</span>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-[#1e3a5f] text-center mb-2">
              {isEditing ? 'Edit Your Profile' : 'Complete Your Profile'}
            </h1>
            <p className="text-[#1e3a5f]/60 text-center mb-6">
              {isEditing ? 'Update your details anytime for better recommendations' : 'Help us personalize your experience'}
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name - Required */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              {/* Email - Display only */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-100 rounded-xl bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <hr className="border-gray-200" />
              
              <p className="text-sm text-[#1e3a5f]/50 text-center">
                Optional - helps us give better recommendations
              </p>

              {/* Role - Optional */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  Role
                </label>
                <div className="relative">
                  <FiBriefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors appearance-none bg-white"
                    data-testid="profile-role-select"
                  >
                    <option value="">Select your role</option>
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Company/Org - Optional */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  Company / Organization
                </label>
                <div className="relative">
                  <FiHome className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Your company or organization"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors"
                    data-testid="profile-company-input"
                  />
                </div>
              </div>

              {/* Industry - Optional */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  Industry
                </label>
                <div className="relative">
                  <FiLayers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors appearance-none bg-white"
                    data-testid="profile-industry-select"
                  >
                    <option value="">Select your industry</option>
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* What is your product/business about */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  What is your product / business about?
                </label>
                <textarea
                  placeholder="e.g. I make small-batch soy candles and want to grow into a retail brand"
                  value={businessDescription}
                  onChange={(e) => setBusinessDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors resize-none"
                  data-testid="profile-business-input"
                />
              </div>

              {/* Stage */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  What stage are you at?
                </label>
                <div className="relative">
                  <FiTrendingUp className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors appearance-none bg-white"
                    data-testid="profile-stage-select"
                  >
                    <option value="">Select your stage</option>
                    {STAGE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1.5">
                  Where are you based?
                </label>
                <div className="relative">
                  <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="e.g. Shadyside, Pittsburgh or Butler County"
                    value={locationField}
                    onChange={(e) => setLocationField(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#1e3a5f] focus:outline-none transition-colors"
                    data-testid="profile-location-input"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-xl text-[#1e3a5f]/70 hover:bg-gray-50 font-medium transition-colors"
                  data-testid="profile-skip-btn"
                >
                  {isEditing ? 'Back to chat' : 'Skip for now'}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#2a4a73] text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="profile-submit-btn"
                >
                  {loading ? 'Saving...' : (isEditing ? 'Save changes' : 'Continue')}
                  {!loading && <FiArrowRight />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
